
import React from "react";
import "./OverView.css";
import { BsCheck2Circle } from "react-icons/bs";

const pillars = [
  {
    title: "1. Enterprise Asset Management (EAM)",
    points: [
      {
        label: "Consolidated Enterprise View",
        text: "Unify disparate asset types and standardize workflows across the entire organization.",
      },
      {
        label: "Lifecycle & Aging Infrastructure Control",
        text: "Extend equipment operational lifespans and maximize return on investment (ROI) by embedding risk management directly into daily business processes.",
      },
    ],
  },
  {
    title: "2. Computerized Maintenance Management System (CMMS)",
    points: [
      {
        label: "Centralized Work Orders & Scheduling",
        text: "Automatically track asset locations, work requests, technician assignments, and history logs in one searchable hub.",
      },
      {
        label: "Preventive & Predictive Maintenance",
        text: "Use real-time IoT diagnostics and AI analytics to detect potential equipment failures before they happen, maintaining warranty compliance and operational stability.",
      },
      {
        label: "Workforce & EHS Compliance",
        text: "Streamline maintenance tasks, improve safety protocols, and maintain audit-ready documentation to meet complex Environmental, Health, and Safety (EHS) requirements.",
      },
    ],
  },
  {
    title: "3. Integrated Asset Accounting",
    points: [
      {
        label: "Automated Depreciation",
        text: "Calculate and deduct depreciation automatically across custom financial schedules without manual calculations.",
      },
      {
        label: "Lifecycle Financial Tracking",
        text: "Audit financial records from initial purchase order to final disposal.",
      },
      {
        label: "Data-Driven Investment Insights",
        text: "Track Total Cost of Ownership (TCO), operational expenses (OpEx), and capital expenditures (CapEx) to guide smart procurement and replacement decisions.",
      },
    ],
  },
];

const assetTypes = [
  {
    label: "Tangible assets",
    text: "Plant machinery, fleet vehicles, buildings, hardware, and infrastructure, including \nlinear assets like Roads, Pipelines, Power Grid etc.",
  },
  {
    label: "Intangible assets",
    text: "Enterprise software licenses, digital services, and operational data,\n Capital work in progress etc.",
  },
];

const WhyAssetMax = () => {
  return (
    <section
      className="WhyAM bg-[#f8fafc] py-12 sm:py-16 font-['Poppins',sans-serif]"
      id="WhyiAssetMax"
    >
      <div className="mx-auto w-full max-w-[110rem] px-5 sm:px-10 lg:px-14">

        {/* Section Header */}
        <div className="mb-10"> 
          {/* <div className="inline-flex items-center rounded-full bg-[#002C78]/10 px-4 py-2 text-xs font-semibold tracking-wider text-[#002C78] mb-5">
            WHY iAssetMax™
          </div> */}

          <h2 className=" mb-6 text-3xl sm:text-3xl lg:text-[2.6rem] font-bold leading-[1.25] text-[#002C78]">
            Why iAssetMax™: Modernizing Enterprise & Maintenance Operations
          </h2>
        <div className="w-24 h-1 rounded-full bg-[#F23100] mb-8"></div>
          {/* <div className="mt-4 h-2 w-20 rounded-full bg-[#F23100]"></div> */}

          <p className=" text-base sm:text-lg leading-8 text-[#4b5563]">
            Moving away from error-prone spreadsheets to a cloud-based Enterprise
            Asset Management (EAM) platform is essential for modern operations.
            iAssetMax™ integrates EAM strategic lifecycle management, CMMS
            maintenance execution, and precise Asset Accounting into a single,
            unified system—giving asset-intensive organizations total visibility,
            improved uptime, and data-driven control.
          </p>
        </div>

        {/* EAM Delivers */}
        <div className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-9 lg:p-12 mb-10 shadow-sm">

          <h3 className="mb-5 text-xl sm:text-2xl font-bold text-[#002C78]">
            What Enterprise Asset Management (EAM) delivers
          </h3>

          <p className="mb-7 text-base sm:text-lg leading-8 text-[#4b5563]">
            EAM coordinates cost, risk, service performance, and sustainability
            across complex operational environments. For asset-heavy
            organizations managing manufacturing plants, transport fleets,
            utility grids, or facilities, iAssetMax™ provides end-to-end
            governance across all physical and digital resources.
          </p>

          <ul className="flex flex-col gap-5">
            {assetTypes.map((a) => (
              <li
                key={a.label}
                className="flex items-start gap-4 text-base sm:text-lg leading-8 text-[#4b5563]"
              >
                <BsCheck2Circle className="mt-1 shrink-0 text-xl text-[#F23100]" />

                <p className="whitespace-pre-line min-w-0">
                  <strong className="font-semibold text-[#002C78]">
                    {a.label}:
                  </strong>{" "}
                  {a.text}
                </p>
              </li>
            ))}
          </ul>
        </div>

        {/* Three Pillars */}
        <div className="mb-8">
          <h3 className="text-2xl sm:text-3xl font-bold text-[#002C78]">
            Three pillars of iAssetMax™
          </h3>
          <div className="mt-4 h-1 w-20 rounded-full bg-[#F23100]"></div>
        </div>

        <div className="flex flex-col gap-6">

          {pillars.map((p) => (
            <article
              key={p.title}
              className="
                grid grid-cols-1
                md:grid-cols-[300px_minmax(0,1fr)]
                lg:grid-cols-[340px_minmax(0,1fr)]
                items-start
                gap-6 md:gap-10 lg:gap-14
                rounded-2xl
                border border-gray-200
                bg-white
                p-6 sm:p-8 lg:p-10
                shadow-sm
                transition-all duration-300
                hover:shadow-md
                hover:border-[#002C78]/30
              "
            >
              {/* Left Heading */}
              <div className="h-full md:border-r md:border-gray-100 md:pr-7 lg:pr-10">
                <h4 className="text-xl sm:text-2xl font-bold leading-[1.4] text-[#002C78]">
                  {p.title}
                </h4>
              </div>

              {/* Right Content */}
              <ul className="flex min-w-0 flex-col gap-5">
                {p.points.map((pt) => (
                  <li
                    key={pt.label}
                    className="flex items-start gap-3 text-base sm:text-lg leading-8 text-[#4b5563]"
                  >
                    <span className="mt-[13px] h-2 w-2 shrink-0 rounded-full bg-[#F23100]"></span>

                    <p className="min-w-0">
                      <strong className="font-semibold text-[#002C78]">
                        {pt.label}:
                      </strong>{" "}
                      {pt.text}
                    </p>
                  </li>
                ))}
              </ul>
            </article>
          ))}

        </div>
      </div>
    </section>
  );
};

export default WhyAssetMax;
